import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, Settings, X, ChevronDown, ChevronRight, FileText, DollarSign, ChevronLeft, Bell, Upload, Database, Edit, MessageSquare, Sparkles, UserCheck, Building2, User, LogOut, Search, BookOpen } from 'lucide-react';
import { fetchUserAttributes, signOut } from '@aws-amplify/auth';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';
import { resolveNativeReportGroups, hasNativeReportGroup, canAccessForecasting } from '../../config/reportGroupAccess';
import hotelIcon from '../../assets/img/Lodgeic-Logo.png';
import hotelIconWhite from '../../assets/img/Lodgeic-Logo-White-Text.png';
import iconTransparent from '../../assets/img/Icon-Transparent.png';
import Cookies from 'js-cookie';
import { reloadForOrgSwitch } from '../../utils/orgSwitchPath';

const NewBadge = () => {
  return (
    <Badge variant="new" className="ml-2 text-[10px] font-bold tracking-wider">
      NEW
    </Badge>
  );
};

// Native report nav (Power BI replacement). Mirrors the PBI report/page names
// exactly so nothing moves for users when the flag flips.
const NATIVE_REPORTS = [
  {
    id: 'native-financial',
    name: 'Financial',
    isNative: true,
    basePath: '/reports/financial',
    pages: [
      { name: 'pnl', displayName: 'Profit + Loss', path: '/reports/financial/pnl' },
      { name: 'trends', displayName: 'Trends', path: '/reports/financial/trends' },
      { name: 'gl-detail', displayName: 'GL Detail', path: '/reports/financial/gl-detail' },
    ],
  },
  {
    id: 'native-operations',
    name: 'Operations',
    isNative: true,
    basePath: '/reports/operations',
    pages: [
      { name: 'labor', displayName: 'Labor Management', path: '/reports/operations/labor' },
      { name: 'reviews', displayName: 'Reviews', path: '/reports/operations/reviews' },
    ],
  },
  {
    id: 'native-revenue',
    name: 'Revenue',
    isNative: true,
    basePath: '/reports/revenue',
    pages: [
      // Overview + Trends live inside the unified Revenue workspace tabs
      { name: 'overview', displayName: 'Revenue', path: '/reports/revenue/overview' },
      { name: 'forecast', displayName: 'Revenue Forecast', path: '/reports/revenue/forecast' },
      { name: 'night-audits', displayName: 'Night Audits', path: '/reports/revenue/night-audits' },
      { name: 'market-performance', displayName: 'Market Performance', path: '/reports/revenue/market-performance' },
    ],
  },
];

const NavItem = ({ icon: Icon, label, isCollapsed, active, hasChildren, isOpen, onToggle, onClick, children, to, showNew }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    if (hasChildren) {
      onToggle();
    } else if (to) {
      navigate(to);
    }
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        onClick={handleClick}
        className={cn(
          'w-full justify-start gap-3 h-11 px-3 font-normal transition-all duration-200',
          active
            ? 'dark:bg-slate-700/50 dark:text-slate-100 dark:hover:bg-slate-700/70'
            : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800/50',
          isCollapsed && 'justify-center px-2'
        )}
        style={active ? { background: 'var(--nav-active-bg)', color: 'var(--nav-active-fg)' } : undefined}
      >
        <Icon size={20} className={cn(
          'shrink-0 transition-colors',
          active ? 'dark:text-slate-100' : 'text-slate-600 dark:text-slate-400'
        )} style={active ? { color: 'var(--nav-active-icon)' } : undefined} />
        {!isCollapsed && (
          <>
            <span className="flex-1 text-left text-sm">{label}</span>
            {showNew && <NewBadge />}
            {hasChildren && (
              <div className="ml-auto">
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            )}
          </>
        )}
      </Button>
      {hasChildren && !isCollapsed && isOpen && (
        <div className="mt-1 space-y-1 pl-9">
          {children}
        </div>
      )}
    </div>
  );
};

const SubNavItem = ({ label, active, to, getState }) => {
  const navigate = useNavigate();

  return (
    <Button
      variant="ghost"
      onClick={() => to && navigate(to, getState ? { state: getState() } : undefined)}
      className={cn(
        'w-full justify-start h-9 px-3 text-sm font-normal transition-all duration-200',
        active
          ? 'dark:bg-slate-700/50 dark:text-slate-100'
          : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/50'
      )}
      style={active ? { background: 'var(--nav-active-bg)', color: 'var(--nav-active-fg)' } : undefined}
    >
      {label}
    </Button>
  );
};

const Sidebar = ({ isCollapsed, onToggle, onHoverChange = () => {}, isMobile = false }) => {
  const [openItems, setOpenItems] = useState({});
  const [reports, setReports] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { userSettings, currentOrganization, organizations, setCurrentOrg } = useUserSettingsContext();
  const isAdmin = userSettings && userSettings.isAdmin === 1;
  const profileDisplayName = `${userSettings?.firstName || ''} ${userSettings?.lastName || ''}`.trim() || userSettings?.name || userEmail || 'Account';
  const profileInitials = profileDisplayName
    .split(/\s+/)
    .map(part => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U';
  const orgDropdownRef = useRef(null);
  const orgSearchInputRef = useRef(null);
  const sidebarRef = useRef(null);
  const dataImportEnabled = useFeatureFlagEnabled('data-import');
  const aiAssistantEnabled = useFeatureFlagEnabled('ai-assistant');
  // Financial Tools sub-page flags
  const journalExportEnabled = useFeatureFlagEnabled('journal-export');
  const forecastingEnabled = useFeatureFlagEnabled('forecasting');
  const laborBudgetingEnabled = useFeatureFlagEnabled('labor-budgeting');
  // Budgeting & Forecasting is granted per user in User setup → Report Access
  // (orgAccess.nativeReports 'forecasting'); the PostHog flags remain as the
  // legacy org-rollout door for everyone who already had access that way.
  const nativeReportGroups = useMemo(
    () => resolveNativeReportGroups(currentOrganization, userSettings),
    [currentOrganization, userSettings]
  );
  const forecastingVisible = canAccessForecasting(nativeReportGroups, forecastingEnabled);
  // Configuration sub-page flags
  const revenueMapEnabled = useFeatureFlagEnabled('revenue-map');
  const journalMapEnabled = useFeatureFlagEnabled('journal-map');
  const positionMapEnabled = useFeatureFlagEnabled('position-map');
  // Parent menus show if any child is enabled
  // Journal has its own sidebar group now — Financial Tools holds the rest.
  // Classic Budgeting (/budgeting) is retired from the nav; the route still opens by URL.
  const financialToolsVisible = forecastingVisible || laborBudgetingEnabled;
  // Chart of Accounts is always available, so Configuration always shows
  const configurationVisible = true;

  // Calculate effective collapse state (collapsed unless hovered; always expanded on mobile)
  const effectiveCollapsed = isMobile ? false : (isCollapsed && !isHovered);

  // Get user email
  useEffect(() => {
    const getUserInfo = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserEmail(attributes.email);
      } catch (error) {
        console.error('Error fetching user attributes:', error);
      }
    };
    getUserInfo();
  }, []);

  // Filter organizations when search term changes
  useEffect(() => {
    if (!organizations) return;
    
    const byName = (a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' });

    if (orgSearchTerm.trim() === '') {
      setFilteredOrgs([...organizations].sort(byName));
    } else {
      const filtered = organizations.filter(org => 
        org.name.toLowerCase().includes(orgSearchTerm.toLowerCase())
      );
      setFilteredOrgs(filtered.sort(byName));
    }
  }, [orgSearchTerm, organizations]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showOrgDropdown && orgSearchInputRef.current) {
      setTimeout(() => {
        orgSearchInputRef.current.focus();
      }, 100);
    } else {
      setOrgSearchTerm('');
    }
  }, [showOrgDropdown]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        setShowOrgDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOrgChange = async (org) => {
    if (!setCurrentOrg) {
      console.error('setCurrentOrg function not available from context.');
      return;
    }
    
    try {
      setCurrentOrg(org.code);
      setShowOrgDropdown(false);
      // Stay on the current page, reloaded clean for the new organization.
      reloadForOrgSwitch(location.pathname);
    } catch (error) {
      console.error('Error changing organization:', error);
    }
    
    setOrgSearchTerm('');
  };

  // Enter picks the org once the search has narrowed to a single match, so a
  // fully typed name doesn't still need a mouse to land on the one result left.
  const handleOrgSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (!orgSearchTerm.trim() || filteredOrgs.length !== 1) return;
    e.preventDefault();
    handleOrgChange(filteredOrgs[0]);
  };

  const clearOrgSearch = () => {
    setOrgSearchTerm('');
    if (orgSearchInputRef.current) {
      orgSearchInputRef.current.focus();
    }
  };

  const handleLogout = async () => {
    try {
      Object.keys(Cookies.get()).forEach(cookieName => {
        Cookies.remove(cookieName, { path: '/' });
        Cookies.remove(cookieName);
      });
      await signOut();
      // Wipe app state but keep the Cognito remembered-device keys (and the
      // remembered email) — clearing deviceKey would force MFA on every future
      // login even when the user asked to remember this device.
      const preserved = Object.entries(localStorage).filter(([key]) =>
        /\.(deviceKey|deviceGroupKey|randomPasswordKey)$/.test(key) ||
        key === 'rememberedEmail' ||
        key === 'mfaDeviceRememberedAt'
      );
      localStorage.clear();
      preserved.forEach(([key, value]) => localStorage.setItem(key, value));
      navigate('/login');
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    if (!currentOrganization || !userSettings || !userSettings.orgAccess) {
      setReports([]);
      return;
    }

    // The user's report grants (mapped server-side to orgAccess.nativeReports
    // groups) decide which native report groups appear. null = unrestricted
    // (grants unresolvable — transition posture). Same resolution the
    // Forecasting gate above uses, so the two can't drift apart.
    const visibleReports = NATIVE_REPORTS.filter((report) =>
      hasNativeReportGroup(nativeReportGroups, report.id.replace('native-', ''))
    );
    setReports(visibleReports);
    setOpenItems(prev => ({
      ...prev,
      ...visibleReports.reduce((acc, report) => ({ ...acc, [`report_${report.id}`]: false }), {}),
    }));
  }, [userSettings, currentOrganization, nativeReportGroups]);

  const handleReportClick = (report) => {
    if (report.pages && report.pages.length > 0) {
      navigate(report.pages[0].path);
    }

    setOpenItems(prev => ({
      ...prev,
      reports: true,
      [`report_${report.id}`]: true
    }));
  };

  const handleMainReportsClick = () => {
    setOpenItems(prev => ({
      ...prev,
      reportsCollapse: !prev.reportsCollapse
    }));
  };

  const toggleItem = (item) => {
    setOpenItems(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  const isReportActive = (reportId) => {
    const native = NATIVE_REPORTS.find(r => r.id === reportId);
    return Boolean(native && location.pathname.startsWith(native.basePath));
  };

  const isPageActive = (reportId, pageName) => {
    const native = NATIVE_REPORTS.find(r => r.id === reportId);
    if (!native) return false;
    const page = native.pages.find(p => p.name === pageName);
    return Boolean(page && location.pathname === page.path);
  };

  const handleMouseEnter = () => {
    if (!isMobile && isCollapsed) {
      setIsHovered(true);
      onHoverChange(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovered(false);
      onHoverChange(false);
    }
  };

  useEffect(() => {
    let timeoutId;
    if (isHovered) {
      onHoverChange(true);
    } else {
      timeoutId = setTimeout(() => {
        onHoverChange(false);
      }, 50);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isHovered, onHoverChange]);

  return (
    <div
      ref={sidebarRef}
      className={cn(
        'border transition-all duration-300 ease-in-out',
        'dark:bg-slate-900 dark:border-slate-700/50',
        'flex flex-col',
        isMobile
          ? 'h-full w-full border-r border-l-0 border-t-0 border-b-0 shadow-none'
          : cn(
              'fixed left-4 top-4 h-[calc(100vh-2rem)] z-[500]',
              'shadow-xl rounded-2xl',
              effectiveCollapsed ? 'w-20' : 'w-64'
            )
      )}
      style={{ background: 'var(--lodge-white)', borderColor: 'var(--lodge-onyx-100)' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Header */}
      <div className={cn(
        "h-20 flex items-center justify-between px-4 py-3 border-b dark:border-slate-800/60",
        !isMobile && "rounded-t-2xl"
      )} style={{ borderColor: 'var(--lodge-onyx-100)' }}>
        {isMobile ? (
          <>
            <div className="flex-1 flex justify-center">
              <img src={hotelIcon} alt="Lodgeic" className="h-124 w-auto object-contain dark:hidden" />
              <img src={hotelIconWhite} alt="Lodgeic" className="h-8 w-auto object-contain hidden dark:block" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="h-8 w-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0"
            >
              <X size={18} className="text-slate-500 dark:text-slate-400" />
            </Button>
          </>
        ) : !effectiveCollapsed ? (
          <>
            <div className="flex-1 flex justify-center">
              <img src={hotelIcon} alt="Lodgeic" className="h-124 w-auto object-contain dark:hidden" />
              <img src={hotelIconWhite} alt="Lodgeic" className="h-8 w-auto object-contain hidden dark:block" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="h-8 w-8 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0"
            >
              {isCollapsed ? (
                <ChevronRight size={18} className="text-slate-500 dark:text-slate-400" />
              ) : (
                <ChevronLeft size={18} className="text-slate-500 dark:text-slate-400" />
              )}
            </Button>
          </>
        ) : (
          <div className="mx-auto">
            <img src={iconTransparent} alt="Lodgeic" className="h-9 w-9" />
          </div>
        )}
      </div>
      
      {/* Navigation */}
      <div className="flex-1 overflow-hidden flex flex-col">
        <ScrollArea className="flex-1">
          <nav className="p-3 space-y-1">
          <NavItem
            icon={Home}
            label="Dashboard"
            isCollapsed={effectiveCollapsed}
            active={location.pathname.includes('/dashboard')}
            to="/dashboard"
          />

          {aiAssistantEnabled && (
            <NavItem
              icon={Sparkles}
              label="Lodgeic AI"
              isCollapsed={effectiveCollapsed}
              active={false}
              onClick={() => window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', { detail: { expanded: true } }))}
              showNew={true}
            />
          )}

          {journalExportEnabled && (
            <>
              <Separator className="my-3" />
              <NavItem
                icon={BookOpen}
                label="Journal"
                isCollapsed={effectiveCollapsed}
                active={location.pathname.includes('/journal-export')}
                hasChildren={true}
                isOpen={openItems.journal}
                onToggle={() => toggleItem('journal')}
              >
                <SubNavItem
                  label="Journal Overview"
                  active={location.pathname === '/journal-export'}
                  to="/journal-export"
                />
                <SubNavItem
                  label="Journal Export"
                  active={location.pathname.includes('/journal-export/detail')}
                  to="/journal-export/detail"
                />
              </NavItem>
            </>
          )}

          {financialToolsVisible && (
            <>
              {!journalExportEnabled && <Separator className="my-3" />}
              <NavItem
                icon={DollarSign}
                label="Financial Tools"
                isCollapsed={effectiveCollapsed}
                active={
                  (forecastingVisible && location.pathname.startsWith('/forecast-v3')) ||
                  (laborBudgetingEnabled && location.pathname.includes('/labor-budgets'))
                }
                hasChildren={true}
                isOpen={openItems.financial}
                onToggle={() => toggleItem('financial')}
              >
                {forecastingVisible && (
                  <SubNavItem
                    label="Budgeting and Forecasting"
                    active={location.pathname.startsWith('/forecast-v3')}
                    to="/forecast-v3"
                  />
                )}
                {laborBudgetingEnabled && (
                  <SubNavItem
                    label="Labor Budgeting"
                    active={location.pathname.includes('/labor-budgets')}
                    to="/labor-budgets"
                  />
                )}
              </NavItem>
            </>
          )}

          {/* Configuration is NOT a Financial Tools child — Chart of Accounts
              is always available. It used to render inside the
              financialToolsVisible fragment, so a user with all three
              Financial Tools gates off lost Configuration too. */}
          {configurationVisible && (
                <NavItem
                  icon={Settings}
                  label="Configuration"
                  isCollapsed={effectiveCollapsed}
                  active={
                    location.pathname.includes('/chart-of-accounts') ||
                    (revenueMapEnabled && location.pathname.includes('/revenue-types')) ||
                    (journalMapEnabled && location.pathname.includes('/journal-types')) ||
                    (positionMapEnabled && location.pathname.includes('/position-department-mapping'))
                  }
                  hasChildren={true}
                  isOpen={openItems.configuration}
                  onToggle={() => toggleItem('configuration')}
                >
                  <SubNavItem
                    label="Chart of Accounts"
                    active={location.pathname.includes('/chart-of-accounts')}
                    to="/chart-of-accounts"
                  />
                  {revenueMapEnabled && (
                    <SubNavItem
                      label="Revenue Map"
                      active={location.pathname.includes('/revenue-types')}
                      to="/revenue-types"
                    />
                  )}
                  {revenueMapEnabled && (
                    <SubNavItem
                      label="Forecast Sources"
                      active={location.pathname.includes('/forecast-sources')}
                      to="/forecast-sources"
                    />
                  )}
                  {journalMapEnabled && (
                    <SubNavItem
                      label="Journal Map"
                      active={location.pathname.includes('/journal-types')}
                      to="/journal-types"
                    />
                  )}
                  {positionMapEnabled && (
                    <SubNavItem
                      label="Position Map"
                      active={location.pathname.includes('/position-department-mapping')}
                      to="/position-department-mapping"
                    />
                  )}
                </NavItem>
          )}

          <Separator className="my-3" />
          
          <NavItem
            icon={FileText}
            label="Reports"
            isCollapsed={effectiveCollapsed}
            active={location.pathname.includes('/report') || location.pathname.includes('/subscriptions')}
            hasChildren={true}
            isOpen={openItems.reportsCollapse}
            onToggle={handleMainReportsClick}
          >
            <SubNavItem
              label="Subscriptions + Alerts"
              active={location.pathname === '/subscriptions'}
              to="/subscriptions"
            />
            {reports.map(report => (
              <div key={report.id} className="space-y-1">
                <Button
                  variant="ghost"
                  onClick={() => handleReportClick(report)}
                  className={cn(
                    'w-full justify-between h-9 px-3 text-sm font-normal',
                    isReportActive(report.id)
                      ? 'dark:bg-slate-700/50 dark:text-slate-100'
                      : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/50'
                  )}
                  style={isReportActive(report.id) ? { background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-500)' } : undefined}
                >
                  <span className="flex-1 text-left">{report.name}</span>
                  {report.pages && report.pages.length > 0 && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItem(`report_${report.id}`);
                      }}
                      className="ml-2 cursor-pointer"
                    >
                      {openItems[`report_${report.id}`] ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </span>
                  )}
                </Button>
                
                {openItems[`report_${report.id}`] && report.pages && report.pages.length > 0 && (
                  <div className="pl-4 space-y-1">
                    {report.pages.map(page => (
                      <Button
                        key={page.name}
                        variant="ghost"
                        onClick={() => page.path && navigate(page.path)}
                        className={cn(
                          'w-full justify-start h-8 px-3 text-xs font-normal',
                          isPageActive(report.id, page.name)
                            ? 'dark:bg-slate-700/50 dark:text-slate-100'
                            : 'text-slate-500 hover:bg-slate-50 dark:text-slate-500 dark:hover:bg-slate-800/50'
                        )}
                        style={isPageActive(report.id, page.name) ? { background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-500)' } : undefined}
                      >
                        {page.displayName}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </NavItem>
          
          {dataImportEnabled && <Separator className="my-3" />}

          {/* ETL Configuration is a single tabbed page — its tabs are the
              navigation, so the sidebar carries no children for it. */}
          {dataImportEnabled && (
            <NavItem
              icon={Database}
              label="ETL Configuration"
              isCollapsed={effectiveCollapsed}
              active={location.pathname.includes('/etl-config')}
              to="/etl-config"
            />
          )}

          <Separator className="my-3" />

          {isMobile && (
            <NavItem
              icon={MessageSquare}
              label="Chat"
              isCollapsed={effectiveCollapsed}
              active={location.pathname.includes('/chat')}
              to="/chat"
            />
          )}

          <NavItem
            icon={Settings}
            label="Settings"
            isCollapsed={effectiveCollapsed}
            active={location.pathname.includes('/settings') && !location.pathname.includes('/admin')}
            to="/settings"
          />
        </nav>
      </ScrollArea>
      
      {/* Bottom Section - Organization & User */}
      <div className="border-t dark:border-slate-800/60 p-3" style={{ borderColor: 'var(--lodge-onyx-100)' }}>
        <div className="relative" ref={orgDropdownRef}>
          {/* Organization/User Button */}
          <button
            onClick={() => setShowOrgDropdown(!showOrgDropdown)}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors",
              effectiveCollapsed && "justify-center"
            )}
          >
            {/* Organization Icon */}
            <div className="w-10 h-10 rounded-lg dark:bg-slate-700 flex items-center justify-center shadow-sm shrink-0" style={{ background: 'var(--lodge-orange-400)' }}>
              <Building2 size={20} className="text-white" />
            </div>
            
            {/* Organization & User Info - only show when expanded */}
            {!effectiveCollapsed && (
              <>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {currentOrganization?.name || 'Select Organization'}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {userEmail || 'Loading...'}
                  </div>
                </div>
                
                {/* Dropdown Indicator */}
                <ChevronDown size={16} className="text-slate-500 dark:text-slate-400 shrink-0" />
              </>
            )}
          </button>

            {/* Dropdown Menu */}
            {showOrgDropdown && (
              <div className={cn(
                "absolute bottom-full mb-2 dark:bg-slate-800/95 rounded-xl shadow-xl border dark:border-slate-700/50 z-50 max-h-[32rem] flex flex-col overflow-hidden",
                effectiveCollapsed ? "left-0 w-72" : "left-0 right-0"
              )} style={{ background: 'var(--lodge-white)', borderColor: 'var(--lodge-onyx-100)' }}>
                {/* User Info Header */}
                {userEmail && (
                  <div className="p-4 border-b border-slate-200 dark:border-slate-700 dark:bg-slate-800/50" style={{ background: 'var(--lodge-onyx-50)' }}>
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 overflow-hidden rounded-full flex items-center justify-center shadow-sm dark:bg-slate-700" style={{ background: 'var(--lodge-onyx-100)' }}>
                        {userSettings?.profilePicture ? (
                          <img src={userSettings.profilePicture} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">{profileInitials}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                          {profileDisplayName}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {userEmail}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Organizations Section */}
                {organizations && organizations.length > 1 && (
                  <>
                    <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 px-1">
                        Organizations
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          ref={orgSearchInputRef}
                          type="text"
                          value={orgSearchTerm}
                          onChange={(e) => setOrgSearchTerm(e.target.value)}
                          onKeyDown={handleOrgSearchKeyDown}
                          placeholder="Search organizations..."
                          className="w-full pl-9 pr-8 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none dark:bg-slate-800 dark:text-slate-200"
                        />
                        {orgSearchTerm && (
                          <button 
                            onClick={clearOrgSearch}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                          >
                            <X className="h-4 w-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Organizations list */}
                    <div className="overflow-y-auto flex-1 max-h-48 py-1">
                      {filteredOrgs.length > 0 ? (
                        filteredOrgs.map((org) => (
                          <button
                            key={org.code}
                            onClick={() => handleOrgChange(org)}
                            className={cn(
                              'w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-3',
                              currentOrganization?.code === org.code
                                ? 'dark:bg-slate-700/50 dark:text-slate-100 font-medium'
                                : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800'
                            )}
                            style={currentOrganization?.code === org.code ? { background: 'var(--lodge-orange-50)', color: 'var(--lodge-orange-600)' } : undefined}
                          >
                            <Building2 size={16} className={currentOrganization?.code === org.code ? 'dark:text-slate-100' : 'text-slate-400'} style={currentOrganization?.code === org.code ? { color: 'var(--lodge-orange-400)' } : undefined} />
                            {org.name}
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-center text-slate-500 dark:text-slate-400 text-sm">
                          No organizations found
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Divider */}
                <Separator className="my-0" />
                
                {/* Account Actions */}
                <div className="py-1">
                  {isAdmin && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setShowOrgDropdown(false);
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors"
                    >
                      <Settings size={16} className="text-slate-500 dark:text-slate-400" />
                      Admin Console
                    </button>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setShowOrgDropdown(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-3 transition-colors"
                  >
                    <LogOut size={16} className="text-slate-500 dark:text-slate-400" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
